<script setup lang="ts">
import { Link, usePage } from "@inertiajs/vue3"
import { ArrowLeftIcon, ChevronRightIcon } from "lucide-vue-next"
import { computed } from "vue"

import { Button } from "@/components/ui/button"
import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination"
import type { PagyMeta } from "@/types/serializers"

const meta = computed(() => (usePage().props?.meta || {}) as PagyMeta)

// series item types:
//   number  → link to that page
//   "N"     → active current page (string of a number)
//   "gap"   → ellipsis (:gap symbol serialized to JSON)

function getPageUrl(n: number): string {
  const url = meta.value.url_template.replace("P", String(n))
  if (n !== 1) return url
  const [base, query] = url.split("?")
  if (!query) return base
  const params = query.split("&").filter((p) => !p.startsWith("page="))
  return params.length ? `${base}?${params.join("&")}` : base
}
</script>

<template>
  <Pagination
    class="items-center justify-between"
    :page="meta.page"
    :sibling-count="3"
    :items-per-page="meta.limit"
    :total="meta.count"
    v-slot="{ page }"
  >
    <p>Página {{ page }} de {{ meta.last }}</p>
    <PaginationContent v-if="meta.last > 1">
      <PaginationPrevious>
        <Button variant="ghost" as-child>
          <Link class="px-0!" :href="getPageUrl(page - 1)">
            <ArrowLeftIcon data-icon="inline-start" class="cn-rtl-flip" />
            <span class="hidden sm:block">Previous</span>
          </Link>
        </Button>
      </PaginationPrevious>

      <template v-for="(item, index) in meta.series" :key="index">
        <PaginationEllipsis v-if="item === 'gap'" :index="index" />
        <!-- Active page: string like "9" -->
        <PaginationItem
          v-else-if="typeof item === 'string'"
          :value="Number(item)"
          :is-active="true"
        >
          {{ item }}
        </PaginationItem>
        <!-- Regular page link: integer -->
        <PaginationItem v-else as-child :value="item" :is-active="false">
          <Link :href="getPageUrl(item)">{{ item }}</Link>
        </PaginationItem>
      </template>

      <PaginationNext>
        <Button variant="ghost" as-child>
          <Link class="px-0!" :href="getPageUrl(page + 1)">
            <span class="hidden sm:block">Next</span>
            <ChevronRightIcon data-icon="inline-end" class="cn-rtl-flip" />
          </Link>
        </Button>
      </PaginationNext>
    </PaginationContent>
  </Pagination>
</template>
